import { Box } from '@mui/system';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Stack,
  tableCellClasses,
  TextField,
  Typography,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { MdEmail, MdTextsms } from 'react-icons/md';
import { RiWhatsappFill } from 'react-icons/ri';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';
import BrudCrumbs from '../../components/BreadCrumbs';
import Label from '../../components/Label';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead, UserMoreMenu } from '../../components/_dashboard/user';
import { fDate, fDateNew } from '../../utils/formatTime';
import BankopsToolbar from './BankopsToolbar';
import { NOT_AVAILABLE } from '../Global/Constants';
import { normalizeValue } from '../../utils/CommonFunction';
import { getBankOpsList } from '../../store/Slice/Bank/BankSlice';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useBankSelector from '../../store/Slice/Bank/BankSelector';
import SimplePagination from '../../components/SimplePagination';
import useDebounce from '../../utils/useDebounce';
import RevenueService from '../services/RevenueService';

const TABLE_HEAD = [
  { id: 'id', label: 'APPLICATION ID', alignRight: false },
  { id: 'lanId', label: 'Lan Id', alignRight: false },
  { id: 'created', label: 'Submission Date', alignRight: false },
  { id: 'name', label: 'NAME', alignRight: false },
  { id: 'companyName', label: 'Company Name', alignRight: false },
  { id: 'contactNumber', label: 'Contact Number', alignRight: false },
  { id: 'sourceFlag', label: 'File Source', alignRight: false },
  { id: 'loanType', label: 'Product', alignRight: false },
  { id: 'city', label: 'City', alignRight: false },
  { id: 'amount', label: 'Amount', alignRight: false },
  { id: 'bankName', label: 'Bank', alignRight: false },
  { id: 'dsaName', label: 'DSA Name', alignRight: false },
  { id: 'status', label: 'status', alignRight: false },
  { id: 'created', label: 'Last update', alignRight: false },
  { id: 'action', label: 'ACTION' }
];

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
  '&:last-child td, &:last-child th': { border: 0 }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 12,
    padding: '6px',
    height: '42.5px',
    fontWeight: 500,
    textTransform: 'uppercase',
    color: '#0d4689'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 11,
    padding: '4px 6px',
    height: '42.5px'
  }
}));

function Bankops() {
  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState();
  const [filterName, setFilterName] = useState('');
  const [sharePopUp, setSharePopUp] = useState(false);
  const [value] = useState(1);
  const [isActive, setActive] = useState(false);
  const [convertLead, setConvertLead] = useState(false);
  const [filterState, setFilterState] = useState({
    product: ' ',
    cityId: null,
    status: ' '
  });
  const [filterDatesRange, setFilterDatesRange] = useState([
    { startDate: null, endDate: null, key: 'selection' }
  ]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const debouncedSearch = useDebounce(filterName, 600);

  const { LoginData } = useLoginSelector();
  const { BankListData } = useBankSelector();
  const { DownloadBankopsRecord } = RevenueService;

  useEffect(() => {
    localStorage.removeItem('selectedItem');
  }, []);

  const getBankListApi = useCallback(() => {
    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      stageId: 58,
      filterModel: {
        bankId: filterState.bankId || null,
        cityId: filterState.cityId || null,
        loanTypeId: filterState.product || null,
        leadStatusId: filterState.status || null,
        bOpsDecisionTypeId: filterState.bOpsDecisionTypeId || null
      },
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        sortOrder: order || 'desc',
        pageSize: rowsPerPage,
        pageNumber: page + 1
      }
    };
    dispatch(getBankOpsList(credentials));
  }, [LoginData, filterState, order, page, rowsPerPage, dispatch, debouncedSearch]);

  useEffect(() => {
    getBankListApi();
  }, [getBankListApi]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, filterState, filterDatesRange, value]);

  useEffect(() => {
    setHasNextPage((BankListData?.length || 0) >= rowsPerPage);
  }, [BankListData, rowsPerPage]);

  const handleRequestSort = useCallback(
    (_, property) => {
      if (property !== 'action') {
        setOrder((prev) => (orderBy === property && prev === 'asc' ? 'desc' : 'asc'));
        setOrderBy(property);
      }
    },
    [orderBy]
  );

  const handleSelectAllClick = useCallback(
    (event) => {
      if (event.target.checked) {
        const list = (BankListData || []).filter((item) => {
          const status = item.logInStatusName?.toLowerCase();
          return status !== 'rejected' && status !== 'sanctioned';
        });
        setSelected(list);
      } else {
        setSelected([]);
      }
    },
    [BankListData]
  );

  const handleClick = useCallback((_, item) => {
    setSelected((prev) => {
      const selectedIndex = prev.indexOf(item);
      if (selectedIndex === -1) return [...prev, item];
      return prev.filter((i) => i !== item);
    });
  }, []);

  const handleDownload = async (selectedItems) => {
    const credentials = {
      userId: LoginData?.userId,
      accessLevelId: 51,
      selectedIds: selectedItems?.map((item) => item.applicationId) || [],
      filterModel: {
        cityId: filterState.cityId || null,
        status: filterState.status || null,
        loanTypeId: filterState.product || null
      }
    };

    try {
      const response = await DownloadBankopsRecord(credentials, LoginData?.jwt);
      const blob = await response.blob();
      const fileName = `BankopsDataDownload_${format(new Date(), 'dd-MM-yyyy_HH-mm-ss')}.xlsx`;
      const url = window.URL.createObjectURL(
        new Blob([blob], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  return (
    <Page title="Partner Management System || Application">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Application" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          <Card>
            <BankopsToolbar
              numSelected={selected.length}
              filterName={filterName}
              setFilterName={setFilterName}
              onFilterName={(e) => setFilterName(e.target.value)}
              filterDatesRange={filterDatesRange}
              setFilterDatesRange={setFilterDatesRange}
              filterState={filterState}
              setFilterState={setFilterState}
              parent="bankops"
              selected={selected}
              handleDownload={handleDownload}
            />
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800, height: '60vh', overflow: 'auto' }}>
                <Table>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={BankListData?.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                    isCheckBox="true"
                  />
                  <TableBody>
                    {BankListData?.map((item) => {
                      const isItemSelected = selected.includes(item);
                      const statusLower = item.logInStatusName?.toLowerCase();
                      const isDisabled = statusLower === 'rejected' || statusLower === 'sanctioned';

                      return (
                        <StyledTableRow
                          hover
                          key={item.applicationId}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              className={isDisabled ? 'disabledDocument' : 'enabledDocument'}
                              checked={isItemSelected}
                              onChange={(e) => handleClick(e, item)}
                            />
                          </TableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.applicationId, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.lanId, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {item?.submissionDate ? fDate(item.submissionDate) : NOT_AVAILABLE}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.leadName, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.companyName, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.contactNumber, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <Label
                              variant="ghost"
                              color={
                                item?.sourceFlag === 'LOGIN_FILE' ||
                                item?.logInStatusName === 'Rejected'
                                  ? 'warning'
                                  : 'primary'
                              }
                              sx={{ opacity: 0.8, fontWeight: 500 }}
                            >
                              {normalizeValue(item?.sourceFlag, NOT_AVAILABLE)}
                            </Label>
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.loanTypeName, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.cityName, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.loanAmount, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.bankPrefName, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(item?.dsaName, NOT_AVAILABLE)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <Label
                              variant="ghost"
                              color={
                                ['u-login', 'sanctioned'].includes(statusLower)
                                  ? 'warning'
                                  : 'success'
                              }
                            >
                              {normalizeValue(
                                item?.logInStatusName?.replace(' ', '\n'),
                                NOT_AVAILABLE
                              )}
                            </Label>
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {normalizeValue(fDateNew(item?.lastUpdatedOn), NOT_AVAILABLE, fDate)}
                          </StyledTableCell>
                          <StyledTableCell align="">
                            <UserMoreMenu rowId={item} parent="bankops" />
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                  {BankListData?.length === 0 && (
                    <TableBody>
                      <TableRow>
                        <StyledTableCell align="center" colSpan={12} sx={{ py: 3 }}>
                          <SearchNotFound searchQuery={filterName} />
                        </StyledTableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>
            {value === 1 && (
              <SimplePagination page={page} setPage={setPage} hasNextPage={hasNextPage} />
            )}
          </Card>
        </Box>

        <Dialog open={sharePopUp} onClose={() => setSharePopUp(false)} fullWidth>
          <DialogTitle className="popupTitle">
            <Typography variant="h6">Select Sharing Option</Typography>
            <IconButton onClick={() => setSharePopUp(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box className="displayFlex alignItemCenter justifyContentCenter" mb={2}>
              {['whatsapp', 'email', 'sms'].map((type) => (
                <Box
                  key={type}
                  className={`mailSMSType mr-2 ${isActive === type ? 'active' : ''}`}
                  onClick={() => setActive(type)}
                >
                  {type === 'whatsapp' && <RiWhatsappFill color="#2CB742" size={48} />}
                  {type === 'email' && <MdEmail color="#0d4689" size={48} />}
                  {type === 'sms' && <MdTextsms color="#4fbbc8" size={48} />}
                </Box>
              ))}
            </Box>
            <FormControl required fullWidth className="formGroup">
              <Typography variant="body1" className="formLabel">
                Email/Mobile
              </Typography>
              <TextField
                fullWidth
                inputProps={{ className: 'textInput' }}
                placeholder="Enter Email/Mobile"
              />
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSharePopUp(false)} variant="contained" className="mainBtn">
              Send
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={convertLead} onClose={() => setConvertLead(false)} fullWidth>
          <DialogContent dividers>
            <Box py={2}>Your Recommendation has been successfully converted to leads.</Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConvertLead(false)} variant="contained" className="mainBtn">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}

export default Bankops;
